/**
 * ============================================================
 * KEJJA OS — src/pages/community/CommunityDetailLayout.tsx
 * Header + 3 tabs (Feed / Goals / Members) for one community.
 * ============================================================
 */

import { NavLink, Outlet, useOutletContext, useParams, useNavigate } from 'react-router-dom';
import {
  MessageSquare, Target, Users, ArrowLeft, Copy, Check, RefreshCw,
  MapPin, Landmark, Heart, Users2, Circle, DoorOpen,
} from 'lucide-react';
import { useState } from 'react';

import { useCommunityContext } from './CommunityLayout';
import { useCommunity, useLeaveCommunity, useRegenerateCode } from '@/hooks/useCommunity';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { CommunityMember, Community } from '@/lib/communityApi';

export interface CommunityDetailContext {
  homeId: string;
  currentUserId: string | undefined;
  community: Community;
  members: CommunityMember[];
  role: string | null;
}

export function useCommunityDetail(): CommunityDetailContext {
  return useOutletContext<CommunityDetailContext>();
}

const TYPE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  chama: Landmark,
  family_extended: Heart,
  neighborhood: MapPin,
  fellowship: Users2,
  general: Circle,
};

export default function CommunityDetailLayout() {
  const outer = useCommunityContext();
  const { communityId } = useParams<{ communityId: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useCommunity(outer.homeId, communityId);
  const leaveMut = useLeaveCommunity(outer.homeId);
  const regenMut = useRegenerateCode(outer.homeId, communityId);

  const [codeCopied, setCodeCopied] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6 text-center text-sm text-muted-foreground">
          Community not found or you're no longer a member.
          <Button variant="link" onClick={() => navigate('/community')} className="mt-2">
            Back to communities
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { community, members } = data;
  const myMembership = members.find((m) => m.home_id === outer.homeId);
  const role = myMembership?.role ?? null;
  const isAdmin = role === 'admin';
  const Icon = TYPE_ICON[community.community_type] ?? Circle;

  const copyCode = async () => {
    if (!community.join_code) return;
    await navigator.clipboard.writeText(community.join_code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 1500);
  };

  const handleLeave = () => {
    if (!confirm(`Leave "${community.name}"? You'll need a new invite code to rejoin.`)) return;
    leaveMut.mutate(community.id, {
      onSuccess: () => navigate('/community'),
    });
  };

  const handleRegen = () => {
    if (!confirm('Regenerate the join code? The old code will stop working immediately.')) return;
    regenMut.mutate();
  };

  const ctx: CommunityDetailContext = {
    homeId: outer.homeId,
    currentUserId: outer.currentUserId,
    community,
    members,
    role,
  };

  return (
    <div className="space-y-4 pb-12">
      <button
        onClick={() => navigate('/community')}
        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
      >
        <ArrowLeft className="w-3 h-3" /> All communities
      </button>

      {/* Community header card */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 flex items-center justify-center flex-shrink-0 text-2xl">
              {community.avatar_emoji ?? <Icon className="w-6 h-6" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-bold text-base truncate">{community.name}</h2>
                <Badge variant="outline" className="text-[9px] h-4 px-1 capitalize">
                  {community.community_type.replace('_', ' ')}
                </Badge>
                {isAdmin && (
                  <Badge variant="outline" className="text-[9px] h-4 px-1 border-rose-300 text-rose-700 dark:text-rose-300">
                    admin
                  </Badge>
                )}
              </div>
              {community.description && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{community.description}</p>
              )}
              <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1"><Users className="w-2.5 h-2.5" /> {members.length} home{members.length === 1 ? '' : 's'}</span>
                {community.location_name && (
                  <span className="flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {community.location_name}</span>
                )}
              </div>
              {community.join_code && (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={copyCode}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted font-mono text-[11px] tracking-wider uppercase hover:bg-muted/70 transition-colors"
                  >
                    {codeCopied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                    {community.join_code}
                  </button>
                  {isAdmin && (
                    <Button
                      size="sm" variant="ghost"
                      className="h-6 text-[10px] text-muted-foreground"
                      onClick={handleRegen}
                      disabled={regenMut.isPending}
                    >
                      <RefreshCw className="w-2.5 h-2.5 mr-0.5" /> Regenerate
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inner tabs */}
      <nav className="flex items-center gap-1 overflow-x-auto border-b border-border">
        <TabLink to={`/community/${community.id}`} end icon={MessageSquare} label="Feed" />
        <TabLink to={`/community/${community.id}/goals`} icon={Target} label="Goals" />
        <TabLink to={`/community/${community.id}/members`} icon={Users} label="Members" count={members.length} />
      </nav>

      <Outlet context={ctx} />

      {/* Leave button at bottom */}
      <div className="pt-6 border-t border-border mt-8">
        <Button
          variant="ghost" size="sm"
          onClick={handleLeave}
          disabled={leaveMut.isPending}
          className="text-muted-foreground hover:text-rose-600 text-xs"
        >
          <DoorOpen className="w-3 h-3 mr-1" />
          Leave community
        </Button>
      </div>
    </div>
  );
}

function TabLink({
  to, end, icon: Icon, label, count,
}: {
  to: string; end?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count?: number;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => cn(
        'flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 -mb-px transition-colors',
        isActive
          ? 'border-rose-500 text-rose-700 dark:text-rose-300'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      {typeof count === 'number' && (
        <span className="text-[10px] tabular-nums bg-muted px-1.5 py-0.5 rounded-full">{count}</span>
      )}
    </NavLink>
  );
}
